"use client";

import React, { useState } from 'react';
import { FiChevronDown, FiArrowRight, FiCheck } from "react-icons/fi";
import Loader from '@/Components/Loader/Loader';
import RatingStar from '@/Components/Common/Icons/RatingStar';
import OutlineStar from '@/Components/Common/Icons/OutlineStar';
import { useDispatch, useSelector } from 'react-redux';
import { openReviewPopup } from '@/Redux/slice/reviewPopupSlice';

/**
 * @param ctaLabel  Override the call-to-action button text. Defaults to
 *                  "Write a Review" so existing FDA product detail pages
 *                  render unchanged. The pipeline detail page passes
 *                  "Share your perspective" because pre-launch drugs
 *                  don't get traditional reviews.
 */
const ViewReviewsLink = ({ rating = 0, reviewCount = 0, onWriteReview, ctaLabel = "Write a Review" }) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch()
  const user = useSelector((state) => state?.userData?.userData?.user);
  const isReps = user?.type === "reps";

  const handleClick = (e) => {
    e.preventDefault();
    setIsLoading(true);

    setTimeout(() => {
      const reviewsSection = document.getElementById('reviews');
      if (reviewsSection) {
        reviewsSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
      setIsLoading(false);
    }, 300);
  };

  const handleOpenReview = (e) => {
    handleClick(e)
    dispatch(openReviewPopup());
  };

  const formatReviewCount = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const renderStars = () => {
    const numRating = Number(rating) || 0;
    const clampedRating = Math.min(Math.max(numRating, 0), 5);
    const fullStars = Math.floor(clampedRating);
    const decimal = clampedRating % 1;
    const hasHalfStar = decimal >= 0.5 && clampedRating < 5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <RatingStar
          key={`full-${i}`}
          className="w-5 h-5"
          fill="#1E5EFF"
          clipId={`view_reviews_star_full_${i}`}
        />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative w-5 h-5">
          <div className="absolute inset-0">
            <OutlineStar className="w-5 h-5 text-gray-300" />
          </div>
          <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
            <RatingStar
              className="w-5 h-5"
              fill="#1E5EFF"
              clipId="view_reviews_star_half"
            />
          </div>
        </div>
      );
    }

    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <OutlineStar
          key={`empty-${i}`}
          className="w-5 h-5 text-gray-300"
        />
      );
    }

    return stars;
  };

  if (reviewCount > 0) {
    return (
      <>
        {isLoading ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4"><Loader /></div>
            <span className="text-sm font-medium font-poppins text-primary-primaryBlue">
              Loading Reviews...
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-end gap-2">
            {/* Rating Row */}
            <a
              href="#reviews"
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleClick}
            >
              <span className="text-2xl font-bold text-gray-900 font-poppins">
                {Number(rating || 0).toFixed(1)}
              </span>
              <div className="flex items-center gap-0.5">
                {renderStars()}
              </div>
              <FiChevronDown className="w-4 h-4 text-gray-600" />
              <span className="text-gray-600 font-poppins text-sm">
                ({formatReviewCount(reviewCount)} {reviewCount === 1 ? 'MD' : 'MDs'})
              </span>
            </a>

            {/* Trust indicator */}
            <span className="text-xs text-gray-500 font-poppins flex items-center gap-1">
              <FiCheck className="w-3.5 h-3.5 text-emerald-500" />
              100% anonymous · NPI-verified
            </span>

            {/* Write Review Button - hidden for reps */}
            {!isReps && (
              <div className="flex items-center justify-end mt-1">
                {onWriteReview ? (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpenReview(e);
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-primaryBlue hover:bg-blue-700 rounded-lg font-poppins transition-colors"
                  >
                    {ctaLabel}
                    <FiArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <a
                    href="#reviews"
                    onClick={handleOpenReview}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-primaryBlue hover:bg-blue-700 rounded-lg font-poppins transition-colors"
                  >
                    {ctaLabel}
                    <FiArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  // No reviews state
  return (
    <>
      {isLoading ? (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4"><Loader /></div>
          <span className="text-sm font-medium font-poppins text-primary-primaryBlue">
            Loading Reviews...
          </span>
        </div>
      ) : (
        <a
          href="#reviews"
          className="inline-flex items-center gap-1 cursor-pointer"
          onClick={handleClick}
        >
          <span className="text-xl font-bold text-gray-900 font-poppins">
            {Number(rating || 0).toFixed(1)}
          </span>
          <div className="flex items-center gap-0.5 mx-1">
            {renderStars()}
          </div>
          <FiChevronDown className="w-4 h-4 text-gray-900" />
        </a>
      )}
    </>
  );
};

export default ViewReviewsLink;
